/**
 * WatchlistIcon — the Worksnip "Watchlist" mark: a folder with an eye inside
 * (a folder of things you're keeping an eye on). Stroke-based so it inherits
 * `currentColor` + sizes like the lucide icons used across the app. Pass
 * `filled` to mark the active/added state (fills the eye pupil).
 */
interface Props {
  size?: number;
  className?: string;
  strokeWidth?: number;
  /** Active state — fills the pupil so a toggled-on Watchlist reads at a glance. */
  filled?: boolean;
  /**
   * "Add" affordance — overlays a small circled-plus badge in the top-right
   * corner so the mark reads as "add to Watchlist". Opt-in (default off) so the
   * nav link + page-title usages stay plain. The folder + eye are scaled to
   * ~90% and nudged toward the bottom-left to clear room for the badge, so the
   * glyph stays legible at the badge's corner rather than colliding with it.
   */
  plus?: boolean;
}

export function WatchlistIcon({ size = 18, className, strokeWidth = 1.75, filled = false, plus = false }: Props) {
  // The eye (lens + pupil) reads better with a thinner outline than the folder,
  // so it sits inside the folder rather than competing with it.
  const eyeStroke = strokeWidth * 0.7;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Folder + eye. When the add-badge is shown, scale the glyph to 90% and
          shift it down-left so the top-right corner is free for the badge. */}
      <g transform={plus ? 'translate(0.4 1.8) scale(0.9)' : undefined}>
        {/* folder */}
        <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
        {/* eye lens — large outlined almond, thinner stroke */}
        <path d="M6.4 14C8.9 10.6 15.1 10.6 17.6 14 15.1 17.4 8.9 17.4 6.4 14Z" strokeWidth={eyeStroke} />
        {/* pupil — small SOLID dot (slightly larger when active). */}
        <circle cx="12" cy="14" r={filled ? 1.9 : 1.5} fill="currentColor" stroke="none" />
      </g>
      {/* Add badge — circled plus in the top-right corner. The disc is filled
          with the surface token so it punches a clean hole over the folder
          beneath it; the ring + plus draw in currentColor on top. */}
      {plus && (
        <g>
          <circle cx="18.5" cy="5.5" r="4.7" fill="var(--ws-surface)" stroke="currentColor" strokeWidth={strokeWidth * 0.9} />
          <path d="M18.5 3.2v4.6M16.2 5.5h4.6" strokeWidth={strokeWidth * 0.9} />
        </g>
      )}
    </svg>
  );
}
